import { Component, EventEmitter, Input, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';
import { Communication } from 'src/providers/communication/communication';
import { Util } from 'src/shared/util/util';

export const SUPPORTED_CHAT_SUGGESTION_TYPES = [
  'cancel_booking',
  'reschedule_booking',
  'send_chat_room_message'
] as const;

export type SupportedChatSuggestionType = typeof SUPPORTED_CHAT_SUGGESTION_TYPES[number];

export interface ChatSuggestion {
  id: number;
  type: string;
  name: string;
  data: {
    message?: string;
    homekeeper_id?: number;
    new_date?: string;
    new_start_time?: string;
    booking_id?: number;
  };
  status: string;
}

export interface ChatSuggestionMessage {
  id: number | null;
  type: string;
  content: {
    chat_suggestion: ChatSuggestion;
  };
  sent_at: string;
  is_read: boolean;
  is_internal: boolean;
  from_member: any;
  was_sent_by: string;
  chat_room_key: string;
  metadata: {
    homekeeper_id?: number;
    address?: { id: number };
    booking?: { id: number };
    job?: {
      id: number;
      date: string;
      start_time: string;
    };
    service_type_details?: {
      name: string;
      key: string;
    };
  };
}

@Component({
  selector: 'chat-suggestion-card',
  templateUrl: 'chat-suggestion-card.component.html',
  styleUrls: ['chat-suggestion-card.component.scss'],
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, TranslationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatSuggestionCardComponent {
  private communication = inject(Communication);
  private util = inject(Util);

  @Input() suggestion: ChatSuggestionMessage;
  @Input() homekeeperName = '';
  @Output() suggestionPerformed = new EventEmitter<void>();
  @Output() suggestionDismissed = new EventEmitter<void>();

  isPerforming = false;
  isDismissing = false;

  get chatSuggestion(): ChatSuggestion | null {
    return this.suggestion?.content?.chat_suggestion || null;
  }

  get isSupportedType(): boolean {
    const name = this.chatSuggestion?.name;
    return name ? SUPPORTED_CHAT_SUGGESTION_TYPES.includes(name as SupportedChatSuggestionType) : false;
  }

  get suggestedMessage(): string {
    return this.chatSuggestion?.data?.message || '';
  }

  get hasRescheduleDateTime(): boolean {
    return !!(this.chatSuggestion?.data?.new_date || this.chatSuggestion?.data?.new_start_time);
  }

  get formattedRescheduleDate(): string {
    const newDate = this.chatSuggestion?.data?.new_date;
    if (!newDate) {
      return '';
    }
    const date = new Date(`${newDate  }T00:00:00`);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  get formattedRescheduleTime(): string {
    const newTime = this.chatSuggestion?.data?.new_start_time;
    if (!newTime) {
      return '';
    }
    const [hours, minutes] = newTime.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  get hasCurrentJobDateTime(): boolean {
    return !!(this.suggestion?.metadata?.job?.date || this.suggestion?.metadata?.job?.start_time);
  }

  get formattedCurrentJobDate(): string {
    const jobDate = this.suggestion?.metadata?.job?.date;
    if (!jobDate) {
      return '';
    }
    const date = new Date(`${jobDate  }T00:00:00`);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  get formattedCurrentJobTime(): string {
    const jobTime = this.suggestion?.metadata?.job?.start_time;
    if (!jobTime) {
      return '';
    }
    const [hours, minutes] = jobTime.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  get serviceTypeName(): string {
    return this.suggestion?.metadata?.service_type_details?.name || '';
  }

  get suggestionTitle(): string {
    const name = this.chatSuggestion?.name || '';
    switch (name) {
      case 'send_chat_room_message':
        const recipient = this.homekeeperName || 'this pro';
        return `Do you want to send a message to ${recipient}?`;
      case 'cancel_booking':
        return 'Do you want to cancel this Job?';
      case 'reschedule_booking':
        return 'Do you want to reschedule this Job?';
      default:
        return 'Suggestion';
    }
  }

  async onSend(): Promise<void> {
    if (this.isPerforming || !this.chatSuggestion?.id) {
      return;
    }
    this.isPerforming = true;
    try {
      const payload = this.buildPayload();
      await this.communication.performAISuggestion(payload, this.chatSuggestion.id);
      this.util.showSuccess(this.getSuccessMessage());
      this.suggestionPerformed.emit();
    } catch (err: any) {
      const errorMessage = err?.error?.message || err?.message || 'Action failed';
      this.util.showError(errorMessage);
    } finally {
      this.isPerforming = false;
    }
  }

  private buildPayload(): { user_data: Record<string, any> } {
    const name = this.chatSuggestion?.name;
    switch (name) {
      case 'cancel_booking':
        return {
          user_data: {
            booking_id: this.chatSuggestion?.data?.booking_id
          }
        };
      case 'reschedule_booking':
        return {
          user_data: {
            booking_id: this.chatSuggestion?.data?.booking_id,
            new_date: this.chatSuggestion?.data?.new_date,
            new_start_time: this.chatSuggestion?.data?.new_start_time
          }
        };
      case 'send_chat_room_message':
      default:
        return { user_data: {} };
    }
  }

  private getSuccessMessage(): string {
    const name = this.chatSuggestion?.name;
    switch (name) {
      case 'cancel_booking':
        return 'Job cancelled successfully';
      case 'reschedule_booking':
        return 'Job rescheduled successfully';
      case 'send_chat_room_message':
        return 'Message sent successfully';
      default:
        return 'Action completed successfully';
    }
  }

  async onDismiss(): Promise<void> {
    if (this.isDismissing || !this.chatSuggestion?.id) {
      return;
    }
    this.isDismissing = true;
    try {
      await this.communication.dismissAISuggestion(this.chatSuggestion.id);
      this.util.showSuccess('Suggestion dismissed');
      this.suggestionDismissed.emit();
    } catch (err: any) {
      const errorMessage = err?.error?.message || err?.message || 'Failed to dismiss suggestion';
      this.util.showError(errorMessage);
    } finally {
      this.isDismissing = false;
    }
  }
}
